import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-md font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cYellow focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-buttonText border-2 border-primary shadow-sm hover:opacity-90 hover:shadow-md disabled:bg-cMediumGrey disabled:text-cDarkGrey disabled:border-cMediumGrey",
        secondary:
          "bg-transparent text-text border-2 border-text hover:bg-secondary/10 disabled:border-cMediumGrey disabled:text-cMediumGrey",
        "secondary-inverse":
          "bg-transparent text-white border-2 border-white hover:bg-secondary/10",
        "primary-inverse":
          "bg-background text-primary border-2 border-primary hover:bg-primary/10",
        tertiary:
          "border-0 text-text shadow-none hover:shadow-md",
        danger:
          "border-2 border-danger text-danger hover:bg-danger hover:text-white disabled:cursor-not-allowed",
        ghost: "hover:bg-secondary/10 hover:text-text",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "py-2 px-4",
        sm: "py-1.5 px-3 text-sm",
        lg: "py-3 px-8 text-lg",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
